import {
  Button,
  DismissIcon,
  Flex,
  Link,
  Modal,
  Skeleton,
  Typography,
} from '@iheartjane/reefer';
import capitalize from 'lodash-es/capitalize';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import {
  ConditionType,
  EXCLUDE_TYPES,
  FORM_FIELD_DEFAULT_MARGIN,
} from '@jane/business-admin/components/shared/specials/modal/form';
import { useGlobalSpecialsModal } from '@jane/business-admin/providers/GlobalSpecialsModalProvider';
import { useSpecialsModal } from '@jane/business-admin/providers/SpecialsModalProvider';

export const Condition = z.object({
  label: z.string(),
  menu_product_id: z.number().optional(),
  value: z.any(),
});

export type Condition = z.infer<typeof Condition>;

interface Props {
  conditions: Condition[];
  conditionsLoading: boolean;
  disableAddButton?: boolean;
  footer?: ReactNode;
  lastCondition?: boolean;
  onConditionsUpdate: (conditions: Condition[]) => void;
  onOpenModal: () => void;
  type:
    | ConditionType.Brands
    | ConditionType.Categories
    | ConditionType.ExcludeBrands
    | ConditionType.ExcludeCategories
    | ConditionType.ExcludeProducts
    | ConditionType.ExcludeWeightValues
    | ConditionType.Products
    | ConditionType.WeightValues;
}

/**
 * Component for each condition line item in building conditions for a Special. This particular component can handle Brands, Categories, Products, or Exclude Products
 * @param props.conditions Simple list of conditions to display, values from a special should be mapped to match a simple conditions array
 * @param props.disableAddButton Disable the "Add Condition" and "Select (condition)" buttons, only allows for removing any existing conditions
 * @param props.footer Rendered beneath the selected conditions, while any are selected
 * @param props.onOpenModal Callback when user clicks "Add condition" or "Select products/brands/categories" buttons
 * @param props.onConditionsUpdate Callback when item is removed by clicking "X" icon, or user clears all by clicking "Remove condition"
 * @param props.type Type of condition
 * @param props.lastCondition Removes bottom border if true
 */
export const ConditionByModal = ({
  conditions: conditionsProp,
  conditionsLoading,
  disableAddButton,
  footer,
  onOpenModal,
  onConditionsUpdate,
  type,
  lastCondition,
}: Props) => {
  const [expanded, setExpanded] = useState(!!conditionsProp.length);
  const [conditions, setConditions] = useState(conditionsProp);

  const {
    posSyncMap: { isJanePosSynced, posSynced },
    isReadOnly,
  } = useSpecialsModal();
  const { isDisabled: globalSpecialDisabled } = useGlobalSpecialsModal();

  useEffect(() => {
    setConditions(conditionsProp);
    setExpanded(!!conditionsProp.length);
  }, [conditionsProp]);

  const removeCondition = (value: string) => {
    const filteredConditions = conditions.filter(
      (condition) => condition.value !== value
    );
    setConditions(filteredConditions);
    setExpanded(!!filteredConditions.length);

    onConditionsUpdate(filteredConditions);
  };

  const removeAllConditions = () => {
    setConditions([]);
    setExpanded(false);

    onConditionsUpdate([]);
  };

  const isAnyPosSynced = isJanePosSynced || posSynced;
  const isDisabled = isAnyPosSynced || isReadOnly || globalSpecialDisabled;

  return (
    <>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mb={FORM_FIELD_DEFAULT_MARGIN}
      >
        <Typography variant="body-semibold">
          {capitalize(
            type === ConditionType.WeightValues
              ? 'Weight'
              : type === ConditionType.ExcludeWeightValues
                ? 'Exclude weights'
                : type
          )}
        </Typography>
        <Flex alignItems="center" gap={24}>
          {expanded && !isDisabled && (
            <Link
              onClick={() => {
                removeAllConditions();
              }}
            >
              Clear all
            </Link>
          )}
          {!expanded && (
            <Button
              label="Add condition"
              onClick={() => {
                onOpenModal();
              }}
              variant={
                EXCLUDE_TYPES.includes(type)
                  ? 'destructive-secondary'
                  : 'secondary'
              }
              disabled={(disableAddButton && !expanded) || isDisabled}
            />
          )}
          {expanded && (
            <Button
              aria-label={`Select ${
                type === ConditionType.ExcludeProducts
                  ? 'products'
                  : type === ConditionType.ExcludeCategories
                    ? 'categories'
                    : type === ConditionType.ExcludeBrands
                      ? 'brands'
                      : type === ConditionType.WeightValues ||
                          type === ConditionType.ExcludeWeightValues
                        ? 'weights'
                        : type
              }`}
              label="Select"
              variant="tertiary"
              onClick={onOpenModal}
              disabled={disableAddButton || isDisabled}
            />
          )}
        </Flex>
      </Flex>
      {expanded && (
        <>
          <Flex gap={12} flexWrap="wrap">
            {conditionsLoading ? (
              <Skeleton animate direction="row">
                <Skeleton.Bone
                  height={48}
                  width={210}
                  mr={24}
                  borderRadius="sm"
                />
                <Skeleton.Bone
                  height={48}
                  width={210}
                  mr={24}
                  borderRadius="sm"
                />
                <Skeleton.Bone height={48} width={210} borderRadius="sm" />
              </Skeleton>
            ) : (
              conditions.map((item) => (
                <Button
                  key={`key_${type}_${item.value}`}
                  variant={
                    EXCLUDE_TYPES.includes(type)
                      ? 'destructive-secondary'
                      : 'secondary'
                  }
                  startIcon={<DismissIcon size="sm" />}
                  label={item.label}
                  onClick={() => removeCondition(item.value)}
                  disabled={isDisabled}
                />
              ))
            )}
          </Flex>
          {footer}
          {!lastCondition && <Modal.ContentDivider />}
        </>
      )}
    </>
  );
};
